import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowUp, Trash2 } from 'lucide-react';
import { collection, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebase';
import { useAuth } from '../AuthContext';
import PostCard from '../components/PostCard';
import { Avatar, EmptyState, Spinner } from '../components/UI';
import { addPostComment, deletePostComment, MAX_COMMENT_LENGTH, subscribeToComments, useAuthorProfiles } from '../services/postInteractions';
import { displayName, formatTime } from '../utils';
import { DISCUSSION_TYPES } from '../locationFeed';

export default function PostDetailPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const endRef = useRef(null);
  const [post, setPost] = useState(undefined);
  const [comments, setComments] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [deletingPost, setDeletingPost] = useState(false);
  const [deletingComment, setDeletingComment] = useState('');
  const [error, setError] = useState('');
  const [commentsError, setCommentsError] = useState('');

  useEffect(() => onSnapshot(doc(db, 'posts', postId), (snapshot) => {
    const data = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
    setPost(DISCUSSION_TYPES.includes(data?.type) ? data : null);
  }, (err) => { setError(err.message || 'Unable to load this post.'); setPost(null); }), [postId]);

  useEffect(() => subscribeToComments(postId, (next) => { setComments(next); setCommentsError(''); }, (err) => setCommentsError(err.message || 'Unable to load comments.')), [postId]);

  useEffect(() => user?.uid ? onSnapshot(collection(db, 'users', user.uid, 'blockedUsers'), (snapshot) => setBlocked(snapshot.docs.map((item) => item.id)), () => setBlocked([])) : undefined, [user?.uid]);

  const visibleComments = comments.filter((comment) => !blocked.includes(comment.userId));
  const authors = useAuthorProfiles(post ? [post.userId] : []);

  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [visibleComments.length]);

  const send = async (event) => {
    event.preventDefault();
    if (!user || sending || !text.trim()) return;
    setSending(true); setError('');
    try { await addPostComment(postId, user, profile, text); setText(''); }
    catch (err) { setError(err.message || 'Unable to post your comment.'); }
    finally { setSending(false); }
  };

  const removeComment = async (comment) => {
    if (!window.confirm('Delete this comment?')) return;
    setDeletingComment(comment.id); setError('');
    try { await deletePostComment(postId, comment.id); }
    catch (err) { setError(err.message || 'Unable to delete this comment.'); }
    finally { setDeletingComment(''); }
  };

  const removePost = async (target) => {
    if (!window.confirm('Delete this post?')) return;
    setDeletingPost(true); setError('');
    try {
      await deleteDoc(doc(db, 'posts', target.id));
      if (target.imagePath) await deleteObject(ref(storage, target.imagePath));
      navigate('/', { replace: true });
    } catch (err) { setError(err.message || 'Unable to fully delete this post.'); setDeletingPost(false); }
  };

  const back = () => (window.history.length > 1 ? navigate(-1) : navigate('/'));

  return <div className="page post-detail-page">
    <header className="chat-header"><button className="icon-button" onClick={back} aria-label="Back"><ArrowLeft /></button><div><h1>Post</h1></div></header>
    <div className="post-detail-body">
      {post === undefined ? <Spinner label="Opening post" />
        : !post || blocked.includes(post.userId) ? <EmptyState title="This post is unavailable" action={<Link className="secondary-button" to="/">Back to feed</Link>} />
        : <>
          <PostCard post={post} author={authors[post.userId]} onDelete={removePost} deleting={deletingPost} detail />
          <section className="post-comments" aria-label="Comments">
            <h2>Comments</h2>
            {commentsError && <p className="form-error" role="alert">{commentsError}</p>}
            <div className={`message-list ${!visibleComments.length ? 'is-empty' : ''}`}>
              {visibleComments.length ? visibleComments.map((comment) => {
                const mine = comment.userId === user?.uid;
                const author = { username: comment.authorUsername, photoURL: comment.authorPhotoURL };
                return <article className={`post-comment ${mine ? 'mine' : ''}`} key={comment.id}>
                  <Link to={`/profile/${comment.userId}`} aria-label={`View @${displayName(author)}`}><Avatar profile={author} size={34} /></Link>
                  <div className="post-comment-bubble">
                    <div className="post-comment-meta"><Link to={`/profile/${comment.userId}`}><strong>@{displayName(author)}</strong></Link><time>{formatTime(comment.createdAt)}</time>{mine && <button className="icon-button danger" onClick={() => removeComment(comment)} disabled={deletingComment === comment.id} aria-label="Delete comment"><Trash2 size={14} /></button>}</div>
                    <p>{comment.text}</p>
                  </div>
                </article>;
              }) : <EmptyState title="No comments yet" />}
              <div ref={endRef} />
            </div>
            {error && <p className="form-error" role="alert">{error}</p>}
            {user
              ? <form className="composer post-comment-composer" onSubmit={send}><textarea rows="1" maxLength={MAX_COMMENT_LENGTH} value={text} onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form.requestSubmit(); } }} placeholder="Add a comment…" aria-label="Add a comment" disabled={sending} /><button className="send-button" disabled={sending || !text.trim()} aria-label="Send comment"><ArrowUp size={20} /></button></form>
              : <p className="post-comment-signin"><Link className="secondary-button" to="/login">Sign in to like and comment</Link></p>}
          </section>
        </>}
    </div>
  </div>;
}
